import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../ui/Logo';
import { useOrg } from '../../contexts/OrgContext';
import { useAppAccess } from '../../hooks/useAppAccess';

interface SidebarItem {
  id: string;
  name: string;
  icon: React.ReactNode;
  route: string;
  disabled?: boolean;
}

interface AppItem {
  id: string;
  name: string;
  icon: string;
  route: string;
  disabled?: boolean;
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  orgId?: string;
  onLogout: () => void;
  showNotifications?: boolean;
  showDiscover?: boolean;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isCollapsed, 
  onToggleCollapse, 
  orgId = 'default',
  onLogout,
  showNotifications = false,
  showDiscover = false,
  isMobile = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrganization } = useOrg();
  const { hasAccess } = useAppAccess();

  // Main navigation items - Clean route patterns (no query parameters)
  const sidebarItems: SidebarItem[] = [
    { 
      id: 'for-you', 
      name: 'For you', 
      icon: '⭐', 
      route: `/o/${orgId}/for-you` 
    },
    { 
      id: 'summary', 
      name: 'Summary', 
      icon: '📊', 
      route: `/o/${orgId}/summary`,
      disabled: true
    },
    { 
      id: 'recent', 
      name: 'Recent', 
      icon: '🕒', 
      route: `/o/${orgId}/recent`,
      disabled: true
    },
    // Only show discover in sidebar if showDiscover is true (homepage app)
    ...(showDiscover ? [{
      id: 'discover', 
      name: 'Discover', 
      icon: '🔍', 
      route: `/o/${orgId}/discover`,
      disabled: true
    }] : []),
    // Only show notifications in sidebar if showNotifications is true (homepage app)
    ...(showNotifications ? [{
      id: 'notifications', 
      name: 'Notifications', 
      icon: '🔔', 
      route: `/o/${orgId}/notifications`,
      disabled: true
    }] : [])
  ];

  // Apps section items - Organization-based routes only
  const appItems: AppItem[] = [
    { id: 'replay', name: 'Replay', icon: '🔄', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/replay` },
    { id: 'reports', name: 'Reports', icon: '📊', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/reports` },
    { id: 'score', name: 'Score', icon: '⚽', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/score` },
    { id: 'capture', name: 'Capture', icon: '📷', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/capture` },
    { id: 'cms', name: 'CMS', icon: '📝', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/cms` },
    { id: 'control-room', name: 'Control Room', icon: '🎮', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/control-room` },
    { id: 'roster', name: 'Roster', icon: '👥', route: orgId === 'default' ? '/select-organization' : `/o/${orgId}/roster` }
  ];

  // Check if current route matches sidebar item
  const isActiveRoute = (route: string) => {
    // Extract the path without query parameters for comparison
    const currentPath = location.pathname;
    const routePath = route.split('?')[0];
    
    // Special handling for homepage route
    if (routePath === `/o/${orgId}`) {
      return currentPath === routePath;
    }
    
    // For all other routes, check if current path starts with route path
    return currentPath.startsWith(routePath);
  };

  // Handle navigation
  const handleNavigation = (route: string) => {
    // Always navigate in the same tab
    navigate(route);
  };

  // Handle logo click - navigate to homepage
  const handleLogoClick = () => {
    // Use the current organization ID if available, otherwise use orgId
    const actualOrgId = currentOrganization?.id || orgId;
    navigate(`/o/${actualOrgId}`);
  };

  return (
    <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative flex flex-col min-h-0 ${
      isMobile 
        ? (isCollapsed ? 'w-0 overflow-hidden' : 'w-80') 
        : (isCollapsed ? 'w-16' : 'w-64')
    } ${isMobile && !isCollapsed ? 'z-50 shadow-lg' : ''}`}>
      <div className="p-3 sm:p-4 flex flex-col h-full min-h-0">
        {/* Logo - Now Clickable */}
        <div className="flex items-center justify-start mb-3 sm:mb-4 flex-shrink-0">
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
            title="Go to Homepage"
            aria-label="Go to Homepage"
          >
            <Logo 
              size="md" 
              variant={isCollapsed ? 'mark' : 'full'} 
              className="flex items-center space-x-2"
            />
          </button>
        </div>

        {/* Expand/Collapse Button - Positioned at the edge */}
        <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-10">
          <button
            onClick={onToggleCollapse}
            className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isCollapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              )}
            </svg>
          </button>
        </div>

        {/* Scrollable nav + apps - allows left panel to scroll when content overflows */}
        <div className="flex-1 min-h-0 overflow-y-auto">
        <nav className="space-y-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (!item.disabled) {
                  handleNavigation(item.route);
                  if (isMobile) {
                    onToggleCollapse(); // Close sidebar on mobile after navigation
                  }
                }
              }}
              disabled={item.disabled}
              className={`w-full flex items-center rounded-lg text-left transition-colors min-h-44 ${
                isCollapsed 
                  ? 'justify-center px-2 py-1.5' 
                  : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                } ${
                  item.disabled
                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                    : isActiveRoute(item.route)
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              <span className="text-sm sm:text-base">{item.icon}</span>
              {!isCollapsed && (
                <span className="text-xs sm:text-sm font-normal">{item.name}</span>
              )}
            </button>
          ))}
        </nav>

        {/* Divider */}
        {!isCollapsed && (
          <div className="border-t border-gray-200 mb-1 flex-shrink-0" />
        )}

        {/* Apps Section - Increased text size for consistency */}
        {!isCollapsed && (
          <div className="flex-shrink-0 mb-1">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 px-2 sm:px-3">
              Apps
            </h3>
            <div className="space-y-1">
              {appItems
                .filter((app) => hasAccess(app.id))
                .map((app) => (
                  <button
                    key={app.id}
                    onClick={() => {
                      if (!app.disabled) {
                        handleNavigation(app.route);
                        if (isMobile) {
                          onToggleCollapse(); // Close sidebar on mobile after navigation
                        }
                      }
                    }}
                    disabled={app.disabled}
                    className={`w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5 rounded-lg transition-colors min-h-44 ${
                      app.disabled 
                        ? 'text-gray-400 cursor-not-allowed opacity-50' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm">{app.icon}</span>
                    <span className="text-xs sm:text-sm font-normal">{app.name}</span>
                  </button>
                ))}
            </div>
          </div>
        )}
        </div>

        {/* Logout Button - Only visible when expanded */}
        {!isCollapsed && (
          <div className="pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onLogout}
              className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors min-h-44"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-xs sm:text-sm font-normal">Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
